import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";

/** Bar fill per tone. Generic names — the consumer maps its own states onto them. */
const TONE_COLORS = {
  primary: "var(--color-primary-default, #9645FF)",
  success: "var(--color-success-600, #15803D)",
  warning: "var(--color-warning-500, #F59E0B)",
  error: "var(--color-error-500, #DC2626)",
};

const DEFAULT_TRACK_COLOR = "var(--color-border-default, #e2e8f0)";

/** Bar height per size — generic name, "default" keeps the original 8px. */
const SIZE_HEIGHT = {
  default: 8,
  sm: 6,
  xs: 4,
};

const BorderLinearProgress = styled(LinearProgress)(() => ({
  height: 8,
  borderRadius: 9999,
  "& .MuiLinearProgress-bar": {
    borderRadius: 9999,
  },
}));

const UIProgressBar = ({
  value,
  showDot,
  color,
  barColor,
  trackColor,
  size,
  sx,
  ...props
}) => {
  const fill = barColor || TONE_COLORS[color] || TONE_COLORS.primary;
  const track = trackColor || DEFAULT_TRACK_COLOR;
  const height = SIZE_HEIGHT[size] || SIZE_HEIGHT.default;

  return (
    <Box sx={{ position: "relative" }}>
      <BorderLinearProgress
        variant="determinate"
        value={value}
        sx={{
          height,
          "&.MuiLinearProgress-colorPrimary": { backgroundColor: track },
          "& .MuiLinearProgress-bar": { backgroundColor: fill },
          ...sx,
        }}
        {...props}
      />
      {showDot && (
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            top: "50%",
            left: `${value}%`,
            transform: "translate(-50%, -50%)",
            width: "12px",
            height: "12px",
            backgroundColor: fill,
            border: "2px solid #fff",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />
      )}
    </Box>
  );
};

UIProgressBar.propTypes = {
  value: PropTypes.number.isRequired,
  showDot: PropTypes.bool,
  /** Bar fill tone — generic names, mapped by the consumer. */
  color: PropTypes.oneOf(["primary", "success", "warning", "error"]),
  /** Explicit bar fill colour; overrides `color` when set. */
  barColor: PropTypes.string,
  /** Track (unfilled) colour; defaults to the neutral border colour. */
  trackColor: PropTypes.string,
  /** Bar height — "default" (8px, unchanged), "sm" (6px) or "xs" (4px). */
  size: PropTypes.oneOf(["default", "sm", "xs"]),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
};

UIProgressBar.defaultProps = {
  showDot: false,
  color: "primary",
  barColor: undefined,
  trackColor: undefined,
  size: "default",
  sx: undefined,
};

export default UIProgressBar;
